/**
 * Submission upload page — premium upload experience.
 */
import { useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';

export function AssignmentSubmit() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function openFilePicker() {
    fileRef.current?.click();
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openFilePicker();
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!assignmentId) return;
    if (files.length === 0) {
      setError('사진을 한 장 이상 선택해 주세요.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('assignment_id', assignmentId);
      for (const f of files) form.append('photos', f);
      const result = await api.postForm<{ submission: { id: string } }>(
        '/api/submissions',
        form,
      );
      navigate(`/student/submissions/${result.submission.id}`, { replace: true });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="page-narrow">
      <Link to="/student" className="back-link">숙제 목록</Link>
      <h1 className="mb-8">숙제 제출</h1>

      <form onSubmit={handleSubmit} data-testid="submit-form">
        <div
          className={`upload-zone${files.length > 0 ? ' has-files' : ''}`}
          role="button"
          tabIndex={0}
          aria-label="사진 업로드 영역 — 클릭하거나 Enter를 눌러 파일을 선택하세요"
          onClick={openFilePicker}
          onKeyDown={handleKeyDown}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            data-testid="photo-input"
            aria-label="풀이 사진 선택"
            onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
          />
          <div className="upload-icon" aria-hidden="true">+</div>
          <p className="upload-label">
            <strong>클릭하여 사진 선택</strong>
          </p>
          <p className="upload-label">풀이 사진을 촬영하거나 업로드하세요</p>
          {files.length > 0 && (
            <p className="upload-count" data-testid="file-count" aria-live="polite">
              {files.length}장 선택됨
            </p>
          )}
        </div>

        {error && (
          <p data-testid="submit-error" className="error-text mt-3" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="submit-button"
          className="btn btn-gold btn-lg w-full mt-5"
          aria-busy={loading}
        >
          {loading ? '채점 중…' : '제출 및 채점'}
        </button>
      </form>
    </main>
  );
}
